import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/App.css';
import SentientOrb from '../orb/SentientOrb.tsx';
import { InquiryChat } from '../orb/SentientOrbChat.tsx';
import AskInput from '../components/AskInput';
import TopBar from '../components/TopBar';
import CourseHistory from '../components/CourseHistory';
import SettingsDemoModal from '../components/SettingsDemoModal';
import { AiOutlineInfoCircle } from 'react-icons/ai';
//import CourseTemplatesLibrary from '../components/CourseTemplatesLibrary';

function Inquiry({
    isAuthenticated,
    currentUser,
    profileLoaded = false,
    onSignOut,
    onNavigateToSignIn,
    onNavigateToSignUp,
    onCourseSelect,
    onNavigateToLearn,
    onNavigateToLanding,
    onNavigateToTermsOfUse,
    onNavigateToPrivacyPolicy,
    currentCourse,
    theme,
    onToggleTheme,
    justSignedIn,
    onClearJustSignedIn,
    onRefreshProfile
}) {
    const [courseHistoryState, setCourseHistoryState] = useState('hidden'); // 'hidden', 'entering', 'visible', 'exiting'
    const [isStreaming, setIsStreaming] = useState(false);
    const [orbAction, setOrbAction] = useState('idle');
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipTimeout, setTooltipTimeout] = useState(null);
    const [showSettingsDemo, setShowSettingsDemo] = useState(false);
    const [settingsDemoChecked, setSettingsDemoChecked] = useState(false);
    const settingsDemoTimeoutRef = useRef(null);
    const orbRef = useRef(null);

    const handleCharacterTyped = useCallback(() => {
        if (orbRef.current) {
            orbRef.current.triggerPulse();
        }
    }, []);

    const handleStreamStart = useCallback(() => {
        setIsStreaming(true);
        setOrbAction('talk');
    }, []);

    const handleStreamComplete = useCallback(() => {
        setIsStreaming(false);
        setOrbAction('idle');
    }, []);

    // Auto-open Course History panel when user just signed in
    useEffect(() => {
        if (justSignedIn && isAuthenticated) {
            // Small delay to let the UI settle
            setTimeout(() => {
                toggleCourseHistory();
                if (onClearJustSignedIn) onClearJustSignedIn(); // Clear the flag
            }, 500);
        }
    }, [justSignedIn, isAuthenticated, onClearJustSignedIn, courseHistoryState]);

    // Show settings demo modal if user hasn't seen it yet
    useEffect(() => {
        // Only run once per session when user profile is loaded
        if (isAuthenticated && profileLoaded && currentUser && !settingsDemoChecked) {
            // Mark as checked immediately to prevent re-runs
            setSettingsDemoChecked(true);

            // Check if settings_demo_show is true (or undefined for backward compatibility)
            const shouldShow = currentUser.settings_demo_show !== false;
            if (shouldShow && !showSettingsDemo) {
                // Clear any existing timeout
                if (settingsDemoTimeoutRef.current) {
                    clearTimeout(settingsDemoTimeoutRef.current);
                }

                // Small delay to let the UI settle
                settingsDemoTimeoutRef.current = setTimeout(() => {
                    setShowSettingsDemo(true);
                    settingsDemoTimeoutRef.current = null;
                }, 1000);
            }
        }

        // Cleanup timeout on unmount
        return () => {
            if (settingsDemoTimeoutRef.current) {
                clearTimeout(settingsDemoTimeoutRef.current);
                settingsDemoTimeoutRef.current = null;
            }
        };
    }, [isAuthenticated, profileLoaded, currentUser, settingsDemoChecked, showSettingsDemo]);

    const toggleCourseHistory = () => {
        if (courseHistoryState === 'hidden') {
            setCourseHistoryState('entering');
            setTimeout(() => setCourseHistoryState('visible'), 10);
        } else if (courseHistoryState === 'visible') {
            setCourseHistoryState('exiting');
            setTimeout(() => setCourseHistoryState('hidden'), 300);
        }
    };

    const closeCourseHistory = () => {
        if (courseHistoryState === 'visible') {
            setCourseHistoryState('exiting');
            setTimeout(() => setCourseHistoryState('hidden'), 300);
        }
    };

    const handleCourseSelectAndClose = (course) => {
        // Close course history panel and then navigate
        closeCourseHistory();
        setTimeout(() => {
            onCourseSelect(course);
        }, 300); // Wait for animation to complete
    };

    const handleTooltipMouseEnter = () => {
        if (tooltipTimeout) {
            clearTimeout(tooltipTimeout);
            setTooltipTimeout(null);
        }
        setShowTooltip(true);
    };

    const handleTooltipMouseLeave = () => {
        // Delay hiding the tooltip to give users time to click the link
        const timeout = setTimeout(() => {
            setShowTooltip(false);
        }, 500); // 500ms delay before hiding
        setTooltipTimeout(timeout);
    };

    const handleTooltipClick = (e) => {
        e.stopPropagation(); // Prevent the click from bubbling to document
        setShowTooltip(!showTooltip);
        if (tooltipTimeout) {
            clearTimeout(tooltipTimeout);
            setTooltipTimeout(null);
        }
    };

    const handleCloseSettingsDemo = () => {
        // Clear any pending timeouts
        if (settingsDemoTimeoutRef.current) {
            clearTimeout(settingsDemoTimeoutRef.current);
            settingsDemoTimeoutRef.current = null;
        }

        // Close modal - settingsDemoChecked is already true from useEffect
        setShowSettingsDemo(false);
    };

    // Close tooltip when clicking anywhere on the page
    useEffect(() => {
        const handleClickOutside = () => {
            if (showTooltip) {
                if (tooltipTimeout) {
                    clearTimeout(tooltipTimeout);
                }
                setShowTooltip(false);
            }
        };

        if (showTooltip) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showTooltip, tooltipTimeout]);

    return (
        <div className="home-container" style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#e8e5da'
        }}>
            <TopBar
                onToggleCourseHistory={toggleCourseHistory}
                isAuthenticated={isAuthenticated}
                currentUser={currentUser}
                onSignOut={onSignOut}
                onNavigateToSignIn={onNavigateToSignIn}
                onNavigateToSignUp={onNavigateToSignUp}
                onNavigateToLearn={onNavigateToLearn}
                theme={theme}
                onToggleTheme={onToggleTheme}
                onRefreshProfile={onRefreshProfile}
            />
            <CourseHistory
                courseHistoryState={courseHistoryState}
                onClose={closeCourseHistory}
                currentUser={currentUser}
                isAuthenticated={isAuthenticated}
                profileLoaded={profileLoaded}
                onCourseSelect={handleCourseSelectAndClose}
                currentCourse={currentCourse}
                onNavigateToLanding={onNavigateToLanding}
            />
            <div className="main-content" style={{
                flex: 1,
                backgroundColor: 'transparent'
            }}>
                <div style={{
                    width: '100%',
                    height: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                }}>
                    <div style={{ width: '100%', height: '300px', marginBottom: '20px' }}>
                        <SentientOrb
                            ref={orbRef}
                            action={orbAction}
                            color="#000"
                        />
                    </div>
                    <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <InquiryChat
                            onStreamComplete={handleStreamComplete}
                            onStreamStart={handleStreamStart}
                            onCharacterTyped={handleCharacterTyped}
                        />
                    </div>
                </div>
                <div style={{
                    position: 'relative',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    paddingTop: '0',
                    marginLeft: '1.5rem'
                }}>
                    <AskInput
                        isAuthenticated={isAuthenticated}
                        currentUser={currentUser}
                        onCourseGenerated={onCourseSelect}
                    />
                    <div
                        className="info-icon-container"
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative'
                        }}
                        onMouseEnter={handleTooltipMouseEnter}
                        onMouseLeave={handleTooltipMouseLeave}
                        onClick={handleTooltipClick}
                    >
                        <AiOutlineInfoCircle size={20} color="#666" />
                        {showTooltip && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '30px',
                                    right: '0',
                                    backgroundColor: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    zIndex: 1000,
                                    fontSize: '14px',
                                    color: '#333',
                                    maxWidth: '280px',
                                    width: 'max-content'
                                }}
                                onMouseEnter={handleTooltipMouseEnter}
                                onMouseLeave={handleTooltipMouseLeave}
                                onClick={(e) => e.stopPropagation()}
                            >
                                AI responses may include mistakes. Check important information.{' '}
                                <a
                                    href="https://docs.teachingorb.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: '#5DADE2',
                                        textDecoration: 'underline',
                                        fontWeight: '500'
                                    }}
                                >
                                    Learn more
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Footer with terms and privacy policy */}
            <div className="home-footer" style={{
                padding: '24px',
                textAlign: 'center',
                borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                backgroundColor: '#e8e5da',
                color: '#666',
                fontSize: '14px'
            }}>
                By using Teaching Orb, you agree to our{' '}
                <span 
                    onClick={onNavigateToTermsOfUse}
                    style={{ 
                        color: '#1a1a1a', 
                        cursor: 'pointer',
                        textDecoration: 'none',
                        fontWeight: '500'
                    }}
                >
                    User Terms
                </span>
                {' '}and have read our{' '}
                <span 
                    onClick={onNavigateToPrivacyPolicy}
                    style={{ 
                        color: '#1a1a1a', 
                        cursor: 'pointer',
                        textDecoration: 'none',
                        fontWeight: '500'
                    }}
                >
                    Privacy Policy
                </span>.
            </div>

            {/* Settings Demo Modal */}
            {/* {showSettingsDemo && isAuthenticated && currentUser && (
                <SettingsDemoModal
                    currentUser={currentUser}
                    onClose={handleCloseSettingsDemo}
                />
            )} */}
        </div>
    );
}

export default Inquiry;